import { RouteComponentProps } from "@reach/router"
import { toSortOrder } from "@shared/platform-api/requests"
import { DocumentTitleOverride, usePaginatedApi } from "@shared/primitive-components"
import {
  DateFilter,
  formatForApi,
  isDateFilter,
  parseDateRange,
  toDateRangeLabel,
} from "@shared/utils/formatters/dates"
import { updateQueryStringParams } from "@shared/utils/helpers/queryStringHelpers"
import { useQueryStringWithStateHook } from "@shared/utils/helpers/useQueryStringWithStateHooks"
import { useState } from "react"
import { SearchTripsInput, Trip, TripSortKeys, tripSortKeysMap, useSearchTrips } from "../../../api/insights"
import { ListWrapperWithContext } from "../../../components/ListWrapperWithContext"
import { usePolicyAndFleet } from "../../../contexts"
import {
  CompletedTripsTable,
  DateRange,
  TripCustomDate,
  tripCustomDateFilterCustomItem,
  tripCustomDateFilterItems,
} from "../../components/SafetyInsightsComponents"
import { PrevPolicyDropdown } from "../../components"

export const Trips: React.FC<RouteComponentProps> = () => {
  const { policy } = usePolicyAndFleet()
  const [isOpen, setIsOpen] = useState(false)
  const defaultDateFilter = DateFilter.LAST_7_DAYS

  const [limit, setLimit] = useQueryStringWithStateHook(`limit`)
  const [sortOrder, setSortOrder] = useQueryStringWithStateHook(`sort_order`)
  const [sortKey, setSortKey] = useQueryStringWithStateHook(`sort_key`)
  const [fromDate, setFromDate] = useQueryStringWithStateHook(`from_date`, defaultDateFilter)
  const [toDate, setToDate] = useQueryStringWithStateHook(`to_date`)
  const [searchTerm, setSearchTerm] = useQueryStringWithStateHook(`term`)

  const queryStringAndStateSetters: Record<string, (newValue: string) => void> = {
    setLimit,
    setSortOrder,
    setSortKey,
    setFromDate,
    setToDate,
    setSearchTerm,
  }

  const { fromDate: parsedFromDate, toDate: parsedToDate } = parseDateRange(fromDate, toDate, defaultDateFilter)

  const input: SearchTripsInput = {
    policyId: policy.id,
    limit: limit && /^[0-9]*$/.test(limit) ? parseInt(limit, 10) : 50,
    sortKey: (sortKey || `finishedAt`) as TripSortKeys,
    sortOrder: toSortOrder(sortOrder),
    startedAtAfter: formatForApi(parsedFromDate),
    startedAtBefore: formatForApi(parsedToDate),
    term: searchTerm || ``,
  }

  const [res, reload] = useSearchTrips(input)
  const { page, dataCount, sort, setSort, pagination, search, mutateRequestInput } = usePaginatedApi<
    Trip,
    SearchTripsInput,
    TripSortKeys,
    TripSortKeys
  >(input, reload, res.result, tripSortKeysMap, queryStringAndStateSetters)

  const setDateRange = (dateRange: DateRange) => {
    const { mutate, currentRequestInput } = mutateRequestInput
    const { fromDate: rawFromDate, toDate: rawToDate } = dateRange
    const { fromDate: newFromDate, toDate: newToDate } = parseDateRange(rawFromDate, rawToDate, defaultDateFilter)

    setFromDate(rawFromDate)
    if (rawToDate !== null) setToDate(rawToDate)

    mutate({
      ...currentRequestInput,
      startedAtAfter: formatForApi(newFromDate),
      startedAtBefore: formatForApi(newToDate),
    })
    updateQueryStringParams(
      {
        from_date: rawFromDate,
        to_date: rawToDate,
      },
      window.location
    )
  }

  return (
    <>
      <DocumentTitleOverride>Flock Motor | Safety Score - Trips</DocumentTitleOverride>
      <ListWrapperWithContext
        title="Trips"
        subTitle="All trips"
        subTitleTooltip="This table shows all the trip data that we have collected. The preset 7-day filter can be changed by using the filters."
        dataCount={dataCount}
        listApiStatus={{
          loading: res.loading,
          failed: res.result?.tag === `err`,
        }}
        filter={{
          defaultDisplayValue: isDateFilter(fromDate) ? fromDate : toDateRangeLabel(parsedFromDate, parsedToDate),
          value: isDateFilter(fromDate) ? fromDate : undefined,
          action: (value: DateFilter) => {
            if (value !== DateFilter.CUSTOM) {
              setDateRange({ fromDate: value, toDate: null })
            } else {
              setIsOpen(true)
            }
          },
          items: tripCustomDateFilterItems,
          lastItem: tripCustomDateFilterCustomItem,
        }}
        search={{
          ...search,
          placeholderText: `Search by VRN`,
        }}
        totalLabels={{
          singular: `Trip`,
          plural: `Trips`,
        }}
        PreviousPolicyDropdown={<PrevPolicyDropdown />}
      >
        <CompletedTripsTable
          page={page}
          pagination={pagination}
          sort={sort}
          sortData={setSort}
          dataCount={dataCount}
          loading={res.loading}
        />
      </ListWrapperWithContext>
      <TripCustomDate
        setDateRange={setDateRange}
        dateRange={{ fromDate: fromDate as string, toDate }}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  )
}
